/**
 * Route Optimizer - client-side TSP solver
 *
 * Optimizes route order using distance/time matrix.
 * Supports up to 10 waypoints with brute-force permutation.
 *
 * Usage:
 * 1. Get distance matrix from Valhalla sources_to_targets
 * 2. Call optimizeRoute() with the matrix
 * 3. Reorder your waypoints according to returned indices
 *
 * Future: supports priority-based optimization (urgent → today → planned → rest)
 */

export interface DistanceMatrixEntry {
  distance: number;
  time: number;
  to_index?: number;
  from_index?: number;
}

export type DistanceMatrix = Array<Array<DistanceMatrixEntry | null>>;

export interface OptimizeOptions {
  /** Fix first point as start (don't permute it) */
  fixStart?: boolean;
  /** Optimize by 'time' or 'distance'. Default: 'time' */
  metric?: 'time' | 'distance';
}

export interface OptimizeResult {
  /** Optimal order of indices */
  order: number[];
  /** Total metric value (time in seconds or distance in km) */
  totalMetric: number;
  /** Number of permutations checked */
  permutationsChecked: number;
}

/**
 * Generate all permutations of an array
 */
function* permutations<T>(arr: T[]): Generator<T[]> {
  if (arr.length <= 1) {
    yield arr;
    return;
  }

  for (let i = 0; i < arr.length; i++) {
    const rest = [...arr.slice(0, i), ...arr.slice(i + 1)];
    for (const perm of permutations(rest)) {
      yield [arr[i], ...perm];
    }
  }
}

/**
 * Get metric value from matrix entry
 */
function getMetric(
  matrix: DistanceMatrix,
  from: number,
  to: number,
  metric: 'time' | 'distance'
): number {
  const entry = matrix[from]?.[to];
  if (!entry) return Infinity;
  return metric === 'time' ? entry.time : entry.distance;
}

/**
 * Calculate total route metric for a given order (no return to start)
 */
function calculateRouteMetric(
  order: number[],
  matrix: DistanceMatrix,
  metric: 'time' | 'distance'
): number {
  let total = 0;
  for (let i = 0; i < order.length - 1; i++) {
    total += getMetric(matrix, order[i], order[i + 1], metric);
  }
  return total;
}

/**
 * Optimize route order using brute-force TSP
 *
 * @param matrix - Distance/time matrix from Valhalla sources_to_targets
 * @param options - Optimization options
 * @returns Optimal order and metrics
 *
 * @example
 * const matrix = await getDistanceMatrix(locations);
 * const result = optimizeRoute(matrix.sources_to_targets, { fixStart: true });
 * const optimizedWaypoints = result.order.map(i => waypoints[i]);
 */
export function optimizeRoute(
  matrix: DistanceMatrix,
  options: OptimizeOptions = {}
): OptimizeResult {
  const { fixStart = false, metric = 'time' } = options;
  const n = matrix.length;

  if (n === 0) {
    return { order: [], totalMetric: 0, permutationsChecked: 0 };
  }

  if (n === 1) {
    return { order: [0], totalMetric: 0, permutationsChecked: 1 };
  }

  if (n === 2) {
    const m = getMetric(matrix, 0, 1, metric);
    return { order: [0, 1], totalMetric: m, permutationsChecked: 1 };
  }

  // For n > 10, warn but still try (might be slow on old devices)
  if (n > 10) {
    console.warn(`[RouteOptimizer] ${n} points may be slow. Consider limiting to 10.`);
  }

  let bestOrder: number[] = [];
  let bestMetric = Infinity;
  let checked = 0;

  if (fixStart) {
    // Fix first point, permute the rest
    const indices = Array.from({ length: n - 1 }, (_, i) => i + 1);

    for (const perm of permutations(indices)) {
      const order = [0, ...perm];
      const total = calculateRouteMetric(order, matrix, metric);
      checked++;

      if (total < bestMetric) {
        bestMetric = total;
        bestOrder = order;
      }
    }
  } else {
    // Permute all points
    const indices = Array.from({ length: n }, (_, i) => i);

    for (const order of permutations(indices)) {
      const total = calculateRouteMetric(order, matrix, metric);
      checked++;

      if (total < bestMetric) {
        bestMetric = total;
        bestOrder = order;
      }
    }
  }

  return {
    order: bestOrder,
    totalMetric: bestMetric,
    permutationsChecked: checked,
  };
}

/**
 * Optimize route with priority groups
 *
 * Points are first grouped by priority, then each group is optimized internally.
 * Groups are visited in priority order (lower priority value = higher priority).
 *
 * @param matrix - Distance/time matrix
 * @param priorities - Priority value for each point (lower = more urgent)
 * @param options - Optimization options
 *
 * @example
 * // Priority: 0 = urgent, 1 = today, 2 = planned, 3 = regular
 * const priorities = [2, 0, 3, 1, 2]; // point indices: 1 is urgent, 3 is today, etc.
 * const result = optimizeRouteWithPriorities(matrix, priorities, { fixStart: true });
 */
export function optimizeRouteWithPriorities(
  matrix: DistanceMatrix,
  priorities: number[],
  options: OptimizeOptions = {}
): OptimizeResult {
  const { fixStart = false, metric = 'time' } = options;
  const n = matrix.length;

  if (n === 0) {
    return { order: [], totalMetric: 0, permutationsChecked: 0 };
  }

  // Group indices by priority
  const groups = new Map<number, number[]>();
  for (let i = 0; i < n; i++) {
    const p = priorities[i] ?? Infinity;
    if (!groups.has(p)) groups.set(p, []);
    groups.get(p)!.push(i);
  }

  // Sort groups by priority (ascending = higher priority first)
  const sortedPriorities = [...groups.keys()].sort((a, b) => a - b);

  const finalOrder: number[] = [];
  let totalMetric = 0;
  let totalChecked = 0;
  let lastPoint: number | null = null;

  for (const priority of sortedPriorities) {
    const groupIndices = groups.get(priority)!;

    if (groupIndices.length === 1) {
      // Single point in group - just add it
      const idx = groupIndices[0];
      if (lastPoint !== null) {
        totalMetric += getMetric(matrix, lastPoint, idx, metric);
      }
      finalOrder.push(idx);
      lastPoint = idx;
      totalChecked++;
      continue;
    }

    // Build sub-matrix for this group
    const subMatrix: DistanceMatrix = groupIndices.map((fromIdx) =>
      groupIndices.map((toIdx) => matrix[fromIdx]?.[toIdx] ?? null)
    );

    // Determine if we should fix start for this group
    const shouldFixStart = fixStart && finalOrder.length === 0;

    // If we have a lastPoint, find best entry point into this group
    let groupStartIdx = 0;
    if (lastPoint !== null && !shouldFixStart) {
      let bestEntryMetric = Infinity;
      for (let i = 0; i < groupIndices.length; i++) {
        const entryMetric = getMetric(matrix, lastPoint, groupIndices[i], metric);
        if (entryMetric < bestEntryMetric) {
          bestEntryMetric = entryMetric;
          groupStartIdx = i;
        }
      }
      totalMetric += bestEntryMetric;
    }

    // Optimize within group
    // If we found a best entry point, fix it as start
    let subResult: OptimizeResult;
    if (lastPoint !== null && !shouldFixStart) {
      // Rotate group so best entry point is first, then fix it
      const rotatedIndices = [
        ...groupIndices.slice(groupStartIdx),
        ...groupIndices.slice(0, groupStartIdx),
      ];
      const rotatedMatrix: DistanceMatrix = rotatedIndices.map((fromIdx) =>
        rotatedIndices.map((toIdx) => matrix[fromIdx]?.[toIdx] ?? null)
      );
      // Use appropriate optimizer based on group size
      if (canOptimizeClientSide(rotatedMatrix.length)) {
        subResult = optimizeRoute(rotatedMatrix, { fixStart: true, metric });
      } else {
        subResult = optimizeRouteLarge(rotatedMatrix, { fixStart: true, metric });
      }
      // Map back to original indices
      const mappedOrder = subResult.order.map((i) => rotatedIndices[i]);
      finalOrder.push(...mappedOrder);
      lastPoint = mappedOrder[mappedOrder.length - 1];
    } else {
      // Use appropriate optimizer based on group size
      if (canOptimizeClientSide(subMatrix.length)) {
        subResult = optimizeRoute(subMatrix, { fixStart: shouldFixStart, metric });
      } else {
        subResult = optimizeRouteLarge(subMatrix, { fixStart: shouldFixStart, metric });
      }
      // Map sub-indices back to original indices
      const mappedOrder = subResult.order.map((i) => groupIndices[i]);
      finalOrder.push(...mappedOrder);
      lastPoint = mappedOrder[mappedOrder.length - 1];
    }

    totalMetric += subResult.totalMetric;
    totalChecked += subResult.permutationsChecked;
  }

  return {
    order: finalOrder,
    totalMetric,
    permutationsChecked: totalChecked,
  };
}

/**
 * Check if brute-force optimization is feasible
 * Returns true for ≤10 points (fast exact solution)
 */
export function canOptimizeClientSide(numPoints: number, _fixStart?: boolean): boolean {
  return numPoints <= 10;
}

/**
 * Nearest Neighbor heuristic for TSP
 * Simple greedy algorithm: always go to the nearest unvisited point
 * O(n²) complexity, deterministic results
 */
export function nearestNeighborTSP(
  matrix: DistanceMatrix,
  options: OptimizeOptions = {}
): OptimizeResult {
  const { fixStart = false, metric = 'time' } = options;
  const n = matrix.length;

  if (n <= 1) {
    return { order: n === 1 ? [0] : [], totalMetric: 0, permutationsChecked: 0 };
  }

  const getMetricValue = (from: number, to: number): number => {
    const entry = matrix[from]?.[to];
    if (!entry) return Infinity;
    return metric === 'time' ? entry.time : entry.distance;
  };

  let bestOrder: number[] = [];
  let bestTotal = Infinity;
  let iterations = 0;

  // Try starting from each point (or just point 0 if fixStart)
  const startPoints = fixStart ? [0] : Array.from({ length: n }, (_, i) => i);

  for (const startPoint of startPoints) {
    const visited = new Set<number>([startPoint]);
    const order: number[] = [startPoint];
    let total = 0;
    let current = startPoint;

    while (visited.size < n) {
      let nearestDist = Infinity;
      let nearest = -1;

      for (let i = 0; i < n; i++) {
        if (!visited.has(i)) {
          const dist = getMetricValue(current, i);
          if (dist < nearestDist) {
            nearestDist = dist;
            nearest = i;
          }
        }
      }

      // If no reachable neighbor found, pick any unvisited point
      if (nearest === -1) {
        for (let i = 0; i < n; i++) {
          if (!visited.has(i)) {
            nearest = i;
            nearestDist = Infinity;
            break;
          }
        }
      }

      if (nearest === -1) break; // All visited

      visited.add(nearest);
      order.push(nearest);
      total += nearestDist;
      current = nearest;
      iterations++;
    }

    // Take first valid order or one with lower total
    if (bestOrder.length === 0 || total < bestTotal) {
      bestTotal = total;
      bestOrder = order;
    }
  }

  return {
    order: bestOrder,
    totalMetric: bestTotal,
    permutationsChecked: iterations,
  };
}

/**
 * 2-opt improvement for TSP
 * Iteratively reverses segments to reduce total distance
 * Improves a given route until no improvement is possible
 */
export function twoOptImprove(
  order: number[],
  matrix: DistanceMatrix,
  options: OptimizeOptions = {}
): OptimizeResult {
  const { fixStart = false, metric = 'time' } = options;
  const n = order.length;

  if (n <= 2) {
    return { order: [...order], totalMetric: 0, permutationsChecked: 0 };
  }

  const getMetricValue = (from: number, to: number): number => {
    const entry = matrix[from]?.[to];
    if (!entry) return Infinity;
    return metric === 'time' ? entry.time : entry.distance;
  };

  const calculateTotal = (route: number[]): number => {
    let total = 0;
    for (let i = 0; i < route.length - 1; i++) {
      total += getMetricValue(route[i], route[i + 1]);
    }
    return total;
  };

  let currentOrder = [...order];
  let improved = true;
  let iterations = 0;
  const maxIterations = n * n * 10; // Safety limit

  // If fixStart, don't swap the first element
  const startIdx = fixStart ? 1 : 0;

  while (improved && iterations < maxIterations) {
    improved = false;

    for (let i = startIdx; i < n - 1; i++) {
      for (let j = i + 1; j < n; j++) {
        iterations++;

        // Create new route by reversing segment between i and j
        const newOrder = [
          ...currentOrder.slice(0, i),
          ...currentOrder.slice(i, j + 1).reverse(),
          ...currentOrder.slice(j + 1),
        ];

        const currentTotal = calculateTotal(currentOrder);
        const newTotal = calculateTotal(newOrder);

        if (newTotal < currentTotal) {
          currentOrder = newOrder;
          improved = true;
          break;
        }
      }
      if (improved) break;
    }
  }

  return {
    order: currentOrder,
    totalMetric: calculateTotal(currentOrder),
    permutationsChecked: iterations,
  };
}

/**
 * Optimized TSP for larger point sets (>10 points)
 * Uses Nearest Neighbor + 2-opt improvement
 * Deterministic and fast: O(n²) + O(n³) worst case
 */
export function optimizeRouteLarge(
  matrix: DistanceMatrix,
  options: OptimizeOptions = {}
): OptimizeResult {
  // Step 1: Get initial solution with Nearest Neighbor
  const nnResult = nearestNeighborTSP(matrix, options);

  // Step 2: Improve with 2-opt
  const improved = twoOptImprove(nnResult.order, matrix, options);

  return {
    order: improved.order,
    totalMetric: improved.totalMetric,
    permutationsChecked: nnResult.permutationsChecked + improved.permutationsChecked,
  };
}

/**
 * Estimate optimization time for given number of points
 * Returns estimated milliseconds on a slow device
 */
export function estimateOptimizationTime(numPoints: number, fixStart = false): number {
  const factorial = (n: number): number => (n <= 1 ? 1 : n * factorial(n - 1));
  const perms = fixStart ? factorial(numPoints - 1) : factorial(numPoints);
  // Rough estimate: 0.001ms per permutation on slow device
  return perms * 0.001;
}
